import { useEffect, useState } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { useOrganization } from '@/contexts/organization-context'
import { api } from '@/services/api'
import { Loader2 } from 'lucide-react'

export function OAuthIntegrationPage() {
  const [searchParams] = useSearchParams()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { currentOrganization } = useOrganization()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [, setIntegration] = useState<any>(null)

  const integrationId = searchParams.get('integration_id')
  const integratorSlug = searchParams.get('slug')
  const presetOAuthUrl = searchParams.get('oauth_url')

  // Check if we're in a popup window
  const isPopup = window.opener !== null

  console.log('=== OAUTH INTEGRATION COMPONENT ===')
  console.log('Current URL:', window.location.href)
  console.log('Integration ID:', integrationId)
  console.log('Integrator slug:', integratorSlug)
  console.log('Preset OAuth URL:', presetOAuthUrl ? 'PROVIDED' : 'NOT PROVIDED')
  console.log('Current organization:', currentOrganization)
  console.log('Org slug from URL:', orgSlug)
  console.log('Is popup window:', isPopup)
  console.log('=================================')

  useEffect(() => {
    // If we have a preset OAuth URL, use it directly without making API calls
    if (presetOAuthUrl) {
      console.log('Using preset OAuth URL, redirecting immediately')
      setLoading(false)
      window.location.href = decodeURIComponent(presetOAuthUrl)
      return
    }
    
    // Fallback to original flow if no preset URL
    if (isPopup) {
      console.log('Popup window detected, proceeding without waiting for organization context')
      initiateOAuth()
    } else if (!currentOrganization) {
      console.log('No active organization, waiting...')
      return
    } else {
      initiateOAuth()
    }
  }, [currentOrganization, integrationId, integratorSlug, isPopup, presetOAuthUrl])

  const initiateOAuth = async () => {
    if (!integrationId || !integratorSlug) {
      setError('Missing integration ID or integrator slug')
      setLoading(false)
      return
    }

    // Get organization and auth info based on whether we're in a popup
    let orgInfo = null
    let authToken = null
    
    if (isPopup) {
      // For popup windows, get auth from parent window's localStorage
      try {
        authToken = window.opener?.localStorage.getItem('authToken')
        const activeOrgSlug = window.opener?.localStorage.getItem('activeOrganization')
        console.log('From parent window - Auth token exists:', !!authToken)
        console.log('From parent window - Active org:', activeOrgSlug)
        
        if (!authToken) {
          setError('No authentication found in parent window')
          setLoading(false)
          return
        }
        
        // For popup windows, we'll use the org slug from URL and get org data from API
        orgInfo = { slug: orgSlug }
      } catch (err) {
        console.error('Error accessing parent window:', err)
        setError('Cannot access parent window authentication')
        setLoading(false)
        return
      }
    } else {
      if (!currentOrganization) {
        setError('No active organization')
        setLoading(false)
        return
      }
      orgInfo = currentOrganization
      authToken = localStorage.getItem('authToken')
    }

    try {
      // Set up headers for API calls
      const headers: any = {}
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }
      if (orgInfo) {
        headers['X-Organization-Slug'] = orgInfo.slug
      }

      // First get the integration details
      console.log('Getting integration details for ID:', integrationId)
      const integrationResponse = await api.get(`/integrations/${integrationId}`, { headers })
      setIntegration(integrationResponse.data)
      
      // Then request OAuth URL from the API 
      console.log('Requesting OAuth URL for integration:', integrationId)
      console.log('Using organization:', orgInfo?.slug)
      
      const response = await api.get(`/oauth/integrations/${integrationId}/oauth_url`, { headers })
      
      if (response.data && response.data.oauth_url) {
        console.log('Redirecting to OAuth provider:', response.data.oauth_url)
        setLoading(false)
        // Redirect to OAuth provider
        window.location.href = response.data.oauth_url
      } else {
        setError('No OAuth URL received from server')
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Failed to get OAuth URL:', err)
      console.error('Error response:', err.response?.data)
      console.error('Error status:', err.response?.status)
      
      // Extract error message from different possible formats
      let errorMessage = 'Unknown error'
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error
        }
      } else {
        errorMessage = err.message
      }
      
      setError(`Failed to get OAuth URL (${err.response?.status || 'Network Error'}): ${errorMessage}`)
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p className="text-gray-600">Initializing OAuth authentication...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-600 mb-4">⚠️ Authentication Error</div>
        <p className="text-gray-600 text-center max-w-md">{error}</p>
        <button 
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.close()}
        >
          Close Window
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-600">Redirecting to authentication provider...</p>
    </div>
  )
}